/**
 * [Digital Salvage] c-collection-tabs
 *
 * 複数のコレクションリンクをタブで切り替えて表示するセクションのコントローラ。
 * - 親 Component から `on:click="/select?id=..."` と `on:keydown="/onKey"` 経由で呼び出される
 * - role="tablist" / "tab" / "tabpanel" + aria-selected / aria-controls / aria-labelledby
 * - 矢印キー / Home / End によるロービングタブインデックス
 * - 初期 active 状態は Liquid 側が data-active を付けて表現する（FOUC 防止）。
 *   JS は data-active のあるタブ/パネルから読み取って tabs/panels の同期を取る。
 */

import { Component } from '@theme/component';

class CollectionTabs extends Component {
  /** @type {HTMLElement[]} */
  tabs = [];

  /** @type {HTMLElement[]} */
  panels = [];

  connectedCallback() {
    super.connectedCallback();
    this.syncRefs();
    this.syncTabLabels();
    this.syncActive();
    this.observePanels();
  }

  updatedCallback() {
    super.updatedCallback?.();
    this.syncRefs();
    this.syncTabLabels();
    this.syncActive();
    this.observePanels();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    if (this.panelObserver) this.panelObserver.disconnect();
  }

  syncRefs() {
    this.tabs = /** @type {HTMLElement[]} */ (
      Array.from(this.querySelectorAll('[role="tab"]'))
    );
    this.panels = /** @type {HTMLElement[]} */ (
      Array.from(this.querySelectorAll('[role="tabpanel"]'))
    );
  }

  /**
   * 各タブボタンのラベルを、対応するパネル（ブロック）の data-tab-label から反映する。
   * Horizon の Section Rendering API はブロック単位で再描画されるため、
   * セクション側 forloop で block.settings.tab_label を出しても更新されない。
   * パネル側に出力された data-tab-label を読み取り、タブ側にミラーリングする。
   */
  syncTabLabels() {
    for (const tab of this.tabs) {
      const id = tab.dataset.tabId;
      if (!id) continue;
      const panel = this.panels.find((p) => p.dataset.tabId === id);
      if (!panel) continue;
      const label = panel.dataset.tabLabel || '';
      const target = tab.querySelector('[data-tab-label-target]');
      if (target instanceof HTMLElement && label) {
        target.textContent = label;
      }
    }
  }

  /**
   * パネル側の data-tab-label が変わったら（テーマエディタでブロック編集時）
   * タブ側にも自動で反映する。
   */
  observePanels() {
    if (this.panelObserver) this.panelObserver.disconnect();
    this.panelObserver = new MutationObserver(() => this.syncTabLabels());
    for (const panel of this.panels) {
      this.panelObserver.observe(panel, {
        attributes: true,
        attributeFilter: ['data-tab-label'],
      });
    }
  }

  /**
   * Liquid が data-active を付けた要素（または最初のタブ）に合わせて状態を同期する。
   */
  syncActive() {
    if (!this.tabs.length) return;
    const activePanel = this.panels.find((panel) => panel.hasAttribute('data-active'));
    const firstTab = this.tabs[0];
    const id =
      (activePanel && activePanel.dataset.tabId) ||
      (firstTab ? firstTab.dataset.tabId : '');
    if (id) this.activate(id);
  }

  /**
   * @param {{ id: string }} data
   */
  select(data) {
    if (!data || !data.id) return;
    this.activate(data.id);
  }

  /**
   * @param {KeyboardEvent} event
   */
  onKey(event) {
    const key = event.key;
    if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(key)) return;
    if (!this.tabs.length) return;

    event.preventDefault();
    const current = this.tabs.findIndex((tab) => tab === document.activeElement);
    let nextIndex = current;
    if (key === 'ArrowRight') nextIndex = (current + 1) % this.tabs.length;
    if (key === 'ArrowLeft') nextIndex = (current - 1 + this.tabs.length) % this.tabs.length;
    if (key === 'Home') nextIndex = 0;
    if (key === 'End') nextIndex = this.tabs.length - 1;

    const nextTab = this.tabs[nextIndex];
    if (!nextTab) return;

    const id = nextTab.dataset.tabId;
    if (id) this.activate(id);
    nextTab.focus();
  }

  /**
   * @param {string} id
   */
  activate(id) {
    for (const tab of this.tabs) {
      const isActive = tab.dataset.tabId === id;
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
      tab.toggleAttribute('data-active', isActive);
    }
    for (const panel of this.panels) {
      const isActive = panel.dataset.tabId === id;
      panel.toggleAttribute('data-active', isActive);
    }
  }
}

if (!customElements.get('c-collection-tabs')) {
  customElements.define('c-collection-tabs', CollectionTabs);
}
